import type { SetStateAction, SyntheticEvent } from 'react';
import React, { useCallback, useState } from 'react';

import { Icon } from '@sravni/react-design-system';
import { ArrowLeft } from '@sravni/react-icons';
import { useBoolean } from '@sravni/react-utils';

import { Currency } from '@src/@types/currency';
import type { ITermRange } from '@src/@types/ranges';
import { BadgeList } from '@src/components/details/components/FilterControl/components/MobileFilterControl/components/BadgeList';
import { FieldControl } from '@src/components/details/components/FilterControl/components/MobileFilterControl/components/FieldControl';
import { FILTERS_NAMES } from '@src/constants/filters';
import {
  sendBackLinkClickEvent,
  sendChangeAmountFilterEvent,
  sendChangeTermFilterEvent,
} from '@src/helpers/analyticsEvents';
import { getAmountString, humanizeTerm } from '@src/helpers/common';
import { getDaysPluralize } from '@src/helpers/strings';
import { useValidatedValue } from '@src/hooks/useValidatedValue';
import { naKartuPageRoute } from '@src/shared/constants/pageRoutes';
import { AMOUNT_BADGE_LIST, TERM_BADGE_LIST } from 'entities/Microcredits/ui';
import { AMOUNT_LABEL, TERM_LABEL } from 'features/FullDeal/config';
import { CurrencySymbol } from 'shared/config/internalization';
import { InputSheet } from 'src/components/details/components/InputSheet';

import styles from './styles.module.scss';

type ActiveField = FILTERS_NAMES.AMOUNT | FILTERS_NAMES.TERM | null;

type Props = {
  initialAmount: number;
  initialTerm: number;
  offerId: string;
  amountMin: number;
  amountMax: number;
  term: ITermRange;
  setTerm: React.Dispatch<SetStateAction<number>>;
  setAmount: React.Dispatch<SetStateAction<number>>;
  amountHelperText: string;
  termsHelperText: string;
  eventLabel: string;
};

export const MobileFilterControl = ({
  initialAmount,
  initialTerm,
  offerId,
  amountMin,
  amountMax,
  term,
  setTerm,
  setAmount,
  amountHelperText,
  termsHelperText,
  eventLabel,
}: Props) => {
  const [isVisible, setVisibility] = useBoolean(false);
  const [activeField, setActiveField] = useState<ActiveField>(null);
  const [error, setError] = useState('');
  const [values, setValues] = useState({ amount: initialAmount, term: initialTerm });

  const termMin = humanizeTerm(term.from, term.fromUnit, { termWithUnit: false });
  const termMax = humanizeTerm(term.to, term.toUnit, { termWithUnit: false });

  const isAmountField = activeField === FILTERS_NAMES.AMOUNT;
  const currentField = isAmountField ? 'amount' : 'term';
  const sheetTitle = isAmountField ? 'Сумма займа' : 'Срок займа';

  const handleOpenSheet = useCallback(
    (field: FILTERS_NAMES.AMOUNT | FILTERS_NAMES.TERM) => {
      setActiveField(field);
      setVisibility.on();
    },
    [setVisibility],
  );

  const handleCloseSheet = useCallback(() => {
    setActiveField(null);
    setVisibility.off();
    setValues((prev) => prev);
  }, [setVisibility]);

  const handleSubmit = () => {
    if (isAmountField) {
      setAmount(values.amount);
      sendChangeAmountFilterEvent(values.amount, 'Карточка детальной информации', offerId);
    } else {
      setTerm(values.term);
      sendChangeTermFilterEvent(values.term, 'Карточка детальной информации', offerId);
    }

    setVisibility.off();
  };

  const handleChange = useCallback(
    (value: number) => {
      setValues((prevValues) => ({
        ...prevValues,
        [currentField]: value,
      }));
    },
    [currentField],
  );

  useValidatedValue({
    value: values[currentField],
    min: isAmountField ? amountMin : Number(termMin),
    max: isAmountField ? amountMax : Number(termMax),
    setError,
    setValue: (value) => setValues((prevValues) => ({ ...prevValues, [currentField]: value })),
    errorMin: isAmountField
      ? `Минимальная сумма от ${getAmountString(amountMin)}${CurrencySymbol.RUB}`
      : `Минимальный срок займа от ${termMin} ${getDaysPluralize(Number(termMin))}`,
    errorMax: isAmountField
      ? `Максимальная сумма до ${getAmountString(amountMax)}${CurrencySymbol.RUB}`
      : `Максимальный срок займа до ${termMax} ${getDaysPluralize(Number(termMax))}`,
  });

  const badges = isAmountField ? AMOUNT_BADGE_LIST : TERM_BADGE_LIST;
  const filteredBadges = badges.filter(({ value }) =>
    isAmountField ? value >= amountMin && value <= amountMax : value >= Number(termMin) && value <= Number(termMax),
  );

  const handleBackLinkClick = (event: SyntheticEvent) => {
    event.stopPropagation();

    sendBackLinkClickEvent(eventLabel);
  };

  return (
    <>
      {initialAmount && initialTerm && (
        <div className={styles.mobileHeader}>
          <div className={styles.badgeContainer}>
            <div className={styles.badge}>
              {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
              <a onClick={handleBackLinkClick} className={styles.link} href={naKartuPageRoute}>
                <Icon icon={<ArrowLeft />} size={20} />
              </a>
            </div>
            <div onClick={() => handleOpenSheet(FILTERS_NAMES.AMOUNT)} className={styles.badge}>
              {`${getAmountString(initialAmount, Currency.RUB)}`}
            </div>
            <div onClick={() => handleOpenSheet(FILTERS_NAMES.TERM)} className={styles.badge}>
              {`${initialTerm} ${getDaysPluralize(initialTerm)}`}
            </div>
          </div>
        </div>
      )}
      <InputSheet
        isButtonSubmitDisabled={Boolean(error)}
        visible={isVisible}
        onClose={handleCloseSheet}
        onSubmit={handleSubmit}
        title={sheetTitle}
      >
        <>
          <FieldControl
            label={isAmountField ? AMOUNT_LABEL : TERM_LABEL}
            value={values[currentField]}
            onChange={handleChange}
            postfix={isAmountField ? CurrencySymbol.RUB : getDaysPluralize(values.term)}
            error={error}
            helperText={isAmountField ? amountHelperText : termsHelperText}
            isAmountField={isAmountField}
          />
          <BadgeList badges={filteredBadges} currentValue={values[currentField]} onSelect={handleChange} />
        </>
      </InputSheet>
    </>
  );
};
